import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDollars } from '@/lib/format'
import type { PurchasableItem } from '@/types'

type Line = { item: PurchasableItem; quantity: number }

type Props = {
  lines: Line[]
  feeCents: number
  totalCents: number
  donationCents?: number | null
  coverFee: boolean
  onCoverFeeChange: (next: boolean) => void
  isFreeCart?: boolean
  actions: ReactNode
  feeHelperText?: string
  showFeeRow?: boolean
}

export default function CartSummary({
  lines,
  feeCents,
  totalCents,
  donationCents,
  coverFee,
  onCoverFeeChange,
  isFreeCart = false,
  actions,
  feeHelperText,
  showFeeRow = true,
}: Props) {
  const hasDonation = donationCents != null && donationCents > 0
  const totalQuantity = lines.reduce((acc, l) => acc + l.quantity, 0)
  const empty = totalQuantity === 0 && !hasDonation

  return (
    <div className="flex h-full flex-col gap-5">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Order</p>
        <ul role="list" className="mt-3 space-y-2 text-sm">
          {lines.length > 0 ? (
            lines.map(({ item, quantity }) => {
              const totalTickets = (item.tickets_per_purchase || 1) * quantity
              const showTicketCount = (item.tickets_per_purchase || 1) > 1
              const lineCents = item.price_cents * quantity
              return (
                <li key={item.id} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-foreground">
                    {item.name}
                    {quantity > 1 && (
                      <span className="ml-1.5 text-muted-foreground">× {quantity}</span>
                    )}
                    {showTicketCount && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        ({totalTickets} tickets)
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums text-foreground">
                    {lineCents === 0 ? 'Free' : formatDollars(lineCents)}
                  </span>
                </li>
              )
            })
          ) : (
            !hasDonation && <li className="text-muted-foreground">No items selected.</li>
          )}
          {hasDonation && (
            <li className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-foreground">Donation</span>
              <span className="shrink-0 tabular-nums text-foreground">
                {formatDollars(donationCents ?? 0)}
              </span>
            </li>
          )}
        </ul>
      </div>

      {showFeeRow && !empty && !isFreeCart && (
        <div className="space-y-1.5">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              <Checkbox
                checked={coverFee}
                onCheckedChange={(v) => onCoverFeeChange(v === true)}
                aria-label="Cover processing fee"
              />
              <span className="text-foreground">Cover processing fee</span>
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {formatDollars(feeCents)}
            </span>
          </label>
          {feeHelperText && (
            <p className="pl-6 text-xs text-muted-foreground">{feeHelperText}</p>
          )}
        </div>
      )}

      <div className="flex items-baseline justify-between border-t border-zinc-950/10 pt-4">
        <span className="text-sm text-muted-foreground">Total</span>
        <span className="text-2xl font-semibold tabular-nums text-foreground">
          {isFreeCart ? 'Free' : formatDollars(totalCents)}
        </span>
      </div>

      {actions && (
        <div className="mt-auto flex flex-col gap-2.5">
          {actions}
          {!isFreeCart && (
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="size-3" /> Secure checkout via Stripe
            </p>
          )}
        </div>
      )}
    </div>
  )
}
